/**
 * Coups de pouce — Attendu 665 « Utiliser la définition de la bissectrice ».
 *
 * Une entrée par `generator_key` (cf. gen_665.js). Une entrée peut être un objet
 * `{ pages }` ou une fonction `(question) => ({ pages })` (cf. utils/coupDePouce.js).
 *
 * Règle absolue : l'aide porte sur d'AUTRES valeurs que celles que le
 * générateur peut tirer, sinon elle donne la réponse.
 * Emphase : **texte** (rendu en gras, pas de HTML brut). Pas de tiret cadratin.
 */

// 74 et 37 : ANGLES_CALCUL va de 20 à 160 de dix en dix, 74 n'y est pas ; et la
// variante inverse tire sa moitié dans [15..75] de cinq en cinq, 37 n'y est pas
// non plus. 114 : la variante config tire son premier angle dans [100..160] de
// dix en dix, 114 n'y est pas. Aucune fiche ne peut coïncider avec un tirage.
const SVG_DIRECTE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 186" role="img"
     aria-label="Un angle XVY de 74 degres partage par sa bissectrice VT en deux angles de 37 degres.">
  <line x1="95" y1="148" x2="205.3" y2="112.2" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="95" y1="148" x2="91.0" y2="32.1" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="95" y1="148" x2="161.5" y2="53.0" stroke="#1D9E75" stroke-width="2.8"/>
  <path d="M 125.4 138.1 A 32 32 0 0 0 113.4 121.8" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <path d="M 113.4 121.8 A 32 32 0 0 0 93.9 116.0" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <path d="M 161.6 126.4 A 70 70 0 0 0 92.6 78.0" fill="none" stroke="#378ADD" stroke-width="1.8"/>
  <text x="144.3" y="81.6" text-anchor="middle" font-size="12.5" font-weight="700" fill="#378ADD">74&#176;</text>
  <text x="132.0" y="124.6" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">37&#176;</text>
  <text x="108.1" y="107.9" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">37&#176;</text>
  <circle cx="95" cy="148" r="3.2" fill="#0f172a"/>
  <text x="214.3" y="117.2" text-anchor="start" font-size="13" font-weight="700" fill="#0f172a">X</text>
  <text x="82.0" y="27.1" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">Y</text>
  <text x="170.5" y="49.0" text-anchor="start" font-size="13" font-weight="700" fill="#1D9E75">T</text>
  <text x="85" y="166" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">V</text>
  <text x="255" y="112" text-anchor="middle" font-size="12" font-weight="700" fill="#0f172a">l’angle entier vaut 74&#176;</text>
  <text x="255" y="132" text-anchor="middle" font-size="12" font-weight="700" fill="#0f172a">74&#176; ÷ 2 = 37&#176;</text>
</svg>`.trim();

const SVG_INVERSE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 186" role="img"
     aria-label="Un angle XVY partage par sa bissectrice VT en deux angles de 37 degres : l angle entier vaut 74 degres.">
  <line x1="95" y1="148" x2="205.3" y2="112.2" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="95" y1="148" x2="91.0" y2="32.1" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="95" y1="148" x2="161.5" y2="53.0" stroke="#1D9E75" stroke-width="2.8"/>
  <path d="M 125.4 138.1 A 32 32 0 0 0 113.4 121.8" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <path d="M 113.4 121.8 A 32 32 0 0 0 93.9 116.0" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <path d="M 161.6 126.4 A 70 70 0 0 0 92.6 78.0" fill="none" stroke="#378ADD" stroke-width="1.8"/>
  <text x="144.3" y="81.6" text-anchor="middle" font-size="12.5" font-weight="700" fill="#378ADD">?</text>
  <text x="132.0" y="124.6" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">37&#176;</text>
  <text x="108.1" y="107.9" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">37&#176;</text>
  <circle cx="95" cy="148" r="3.2" fill="#0f172a"/>
  <text x="214.3" y="117.2" text-anchor="start" font-size="13" font-weight="700" fill="#0f172a">X</text>
  <text x="82.0" y="27.1" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">Y</text>
  <text x="170.5" y="49.0" text-anchor="start" font-size="13" font-weight="700" fill="#1D9E75">T</text>
  <text x="85" y="166" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">V</text>
  <text x="255" y="112" text-anchor="middle" font-size="12" font-weight="700" fill="#0f172a">chaque moitié vaut 37&#176;</text>
  <text x="255" y="132" text-anchor="middle" font-size="12" font-weight="700" fill="#0f172a">37&#176; × 2 = 74&#176;</text>
</svg>`.trim();

const SVG_CONFIG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 216" role="img"
     aria-label="Un angle plat XVZ coupe par VY. XVY vaut 114 degres, donc YVZ vaut 66 degres, et sa bissectrice VT le partage en deux angles de 33 degres.">
  <line x1="180" y1="150" x2="300.0" y2="150.0" stroke="#0f172a" stroke-width="2.4"/>
  <line x1="180" y1="150" x2="60.0" y2="150.0" stroke="#0f172a" stroke-width="2.4"/>
  <line x1="180" y1="150" x2="131.2" y2="40.4" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="180" y1="150" x2="245.4" y2="49.4" stroke="#1D9E75" stroke-width="2.8"/>
  <path d="M 156.4 97.0 A 58 58 0 0 0 122.0 150.0" fill="none" stroke="#9333EA" stroke-width="2.2"/>
  <path d="M 212.0 150.0 A 32 32 0 0 0 197.4 123.2" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <path d="M 197.4 123.2 A 32 32 0 0 0 167.0 120.8" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <text x="117.9" y="113.7" text-anchor="middle" font-size="12" font-weight="700" fill="#9333EA">114&#176;</text>
  <text x="220.4" y="132.1" text-anchor="middle" font-size="11.5" font-weight="700" fill="#F97316">33&#176;</text>
  <text x="183.6" y="108.1" text-anchor="middle" font-size="11.5" font-weight="700" fill="#F97316">33&#176;</text>
  <circle cx="180" cy="150" r="3.2" fill="#0f172a"/>
  <text x="51.0" y="155.0" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">X</text>
  <text x="123.2" y="35.4" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">Y</text>
  <text x="254.4" y="45.4" text-anchor="start" font-size="13" font-weight="700" fill="#1D9E75">T</text>
  <text x="309.0" y="155.0" text-anchor="start" font-size="13" font-weight="700" fill="#0f172a">Z</text>
  <text x="174" y="168" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">V</text>
  <text x="180" y="190" text-anchor="middle" font-size="11.5" font-weight="700" fill="#9333EA">180&#176; − 114&#176; = 66&#176;</text>
  <text x="180" y="208" text-anchor="middle" font-size="11.5" font-weight="700" fill="#F97316">puis 66&#176; ÷ 2 = 33&#176;</text>
</svg>`.trim();

// 86 et 43 : ANGLES_CONSTRUCTION va de 30 à 150 de dix en dix, donc ni l'angle
// entier ni sa moitié ne peuvent tomber sur ces valeurs.
const SVG_CONSTRUCTION = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 186" role="img"
     aria-label="Le rapporteur est centre sur le sommet V, sa base posee sur le cote VX. L angle XVY mesure 86 degres ; on trace la demi-droite VT a 43 degres, qui le partage en deux angles de 43 degres.">
  <path d="M 16 162 A 72 72 0 0 1 160 162" fill="#DBEAFE" fill-opacity="0.55" stroke="#378ADD" stroke-width="1.3"/>
  <line x1="154.0" y1="162.0" x2="160.0" y2="162.0" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="153.0" y1="150.5" x2="158.9" y2="149.5" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="150.0" y1="139.4" x2="155.7" y2="137.4" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="145.2" y1="129.0" x2="150.4" y2="126.0" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="138.6" y1="119.6" x2="143.2" y2="115.7" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="130.4" y1="111.4" x2="134.3" y2="106.8" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="121.0" y1="104.8" x2="124.0" y2="99.6" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="110.6" y1="100.0" x2="112.6" y2="94.3" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="99.5" y1="97.0" x2="100.5" y2="91.1" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="88.0" y1="96.0" x2="88.0" y2="90.0" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="76.5" y1="97.0" x2="75.5" y2="91.1" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="65.4" y1="100.0" x2="63.4" y2="94.3" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="55.0" y1="104.8" x2="52.0" y2="99.6" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="45.6" y1="111.4" x2="41.7" y2="106.8" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="37.4" y1="119.6" x2="32.8" y2="115.7" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="30.8" y1="129.0" x2="25.6" y2="126.0" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="26.0" y1="139.4" x2="20.3" y2="137.4" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="23.0" y1="150.5" x2="17.1" y2="149.5" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="22.0" y1="162.0" x2="16.0" y2="162.0" stroke="#378ADD" stroke-width="0.7"/>
  <line x1="88" y1="162" x2="198.0" y2="162.0" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="88" y1="162" x2="95.7" y2="52.3" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="88" y1="162" x2="168.4" y2="87.0" stroke="#1D9E75" stroke-width="2.8"/>
  <path d="M 118.0 162.0 A 30 30 0 0 0 109.9 141.5" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <path d="M 109.9 141.5 A 30 30 0 0 0 90.1 132.1" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <text x="128.9" y="149.9" text-anchor="middle" font-size="11.5" font-weight="700" fill="#F97316">43&#176;</text>
  <text x="106.9" y="126.3" text-anchor="middle" font-size="11.5" font-weight="700" fill="#F97316">43&#176;</text>
  <circle cx="93.0" cy="90.2" r="4" fill="#378ADD" stroke="#ffffff" stroke-width="1.6"/>
  <circle cx="140.7" cy="112.9" r="4" fill="#1D9E75" stroke="#ffffff" stroke-width="1.6"/>
  <circle cx="88" cy="162" r="3.2" fill="#0f172a"/>
  <text x="206.0" y="167.0" text-anchor="start" font-size="13" font-weight="700" fill="#0f172a">X</text>
  <text x="87.7" y="48.3" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">Y</text>
  <text x="176.4" y="84.0" text-anchor="start" font-size="13" font-weight="700" fill="#1D9E75">T</text>
  <text x="80" y="178" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">V</text>
  <text x="278" y="78" text-anchor="middle" font-size="11.5" font-weight="700" fill="#378ADD">la base sur [VX)</text>
  <text x="278" y="100" text-anchor="middle" font-size="11.5" font-weight="700" fill="#378ADD">l’angle mesure 86&#176;</text>
  <text x="278" y="122" text-anchor="middle" font-size="11.5" font-weight="700" fill="#F97316">86&#176; ÷ 2 = 43&#176;</text>
  <text x="278" y="144" text-anchor="middle" font-size="11.5" font-weight="700" fill="#1D9E75">on trace à 43&#176;</text>
</svg>`.trim();

const CP_665 = {
    // ENTRÉE FONCTION : les trois variantes ne font pas le même calcul. Une fiche
    // unique montrerait deux opérations inutiles sur trois.
    // Trois variantes du générateur, trois aides : la configuration en angle
    // plat, la lecture directe (l\'angle entier est donné) et la lecture
    // inverse (une part est donnée), qui sert de défaut.
    calcul_bissectrice_qcm_unique: {
        variantes: [
            {
                sousType: 'config',
                pages: [
                    { titre: 'La règle', lignes: [
                        'Deux angles qui forment un angle **plat** ont pour somme **180°** : le second vaut 180° moins le premier.',
                        'La **bissectrice** partage ensuite ce second angle en **deux parts égales** : chacune en est la **moitié**.',
                    ] },
                    { titre: 'Un exemple', svg: SVG_CONFIG },
                ],
            },
            {
                sousType: 'directe',
                pages: [
                    { titre: 'La règle', lignes: [
                        'La **bissectrice** partage l\'angle en **deux parts égales**.',
                        'Si l\'on connaît l\'angle entier, chaque part en vaut la **moitié**.',
                    ] },
                    { titre: 'Un exemple', svg: SVG_DIRECTE },
                ],
            },
        ],
        defaut: {
            pages: [
                { titre: 'La règle', lignes: [
                    'La **bissectrice** partage l\'angle en **deux parts égales**.',
                    'Si l\'on connaît une part, l\'angle entier en vaut le **double**.',
                ] },
                { titre: 'Un exemple', svg: SVG_INVERSE },
            ],
        },
    },

    // La méthode vient de l'énoncé, d'où elle a été retirée : elle donnait le
    // programme complet en clair à qui n'en avait pas besoin.
    construction_bissectrice_programme_construction: {
        pages: [
            { titre: 'La règle', lignes: [
                'La **bissectrice** partage l\'angle en **deux angles de même mesure**.',
                'Pose le centre du rapporteur sur le **sommet** de l\'angle et sa base sur **un de ses deux côtés**.',
                'Lis la mesure de l\'angle entier, puis **divise-la par 2**.',
                'Reporte ce résultat **à partir du côté sur lequel tu as posé la base** : tu obtiens la bissectrice.',
            ] },
            { titre: 'Un exemple', svg: SVG_CONSTRUCTION },
        ],
    },
};

export default CP_665;
